/**
 * Role policy for module-limited Django groups.
 *
 * Manning and Upload grant only their own sections. Staff and Supervisor
 * (and other operational groups) keep existing app-wide access.
 */
export const MANNING_GROUP = 'Manning';
export const UPLOAD_GROUP = 'Upload';

export const BROAD_ACCESS_GROUPS = Object.freeze([
    'Supervisor',
    'AMPT',
    'AMSS',
    'AI',
    'Flight Planning',
    'Scheduler',
    'Viewer',
    'Inspector',
    'ATLAS Enterprise',
]);

export function canAccessManning({ isStaff, groups = [] }) {
    return Boolean(isStaff || groups.includes('Supervisor') || groups.includes(MANNING_GROUP));
}

export function canAccessUploadData({ isStaff, groups = [] }) {
    return Boolean(isStaff || groups.includes('Supervisor') || groups.includes(UPLOAD_GROUP));
}

export function isModuleLimitedUser({ isStaff, groups = [] }) {
    if (isStaff) return false;
    if (groups.some((g) => BROAD_ACCESS_GROUPS.includes(g))) return false;
    return groups.includes(MANNING_GROUP) || groups.includes(UPLOAD_GROUP);
}

export function defaultHomePath({ isStaff, groups = [] }) {
    if (!isModuleLimitedUser({ isStaff, groups })) return '/';
    const manning = groups.includes(MANNING_GROUP);
    const upload = groups.includes(UPLOAD_GROUP);
    if (manning && !upload) return '/manning';
    if (upload && !manning) return '/upload-data';
    return '/';
}

export function isPathAllowedForUser(pathname, { isStaff, groups = [] }) {
    if (!isModuleLimitedUser({ isStaff, groups })) return true;
    if (
        pathname === '/'
        || pathname.startsWith('/change-password')
        || pathname.startsWith('/notifications')
    ) {
        return true;
    }
    if (groups.includes(MANNING_GROUP) && pathname.startsWith('/manning')) return true;
    if (groups.includes(UPLOAD_GROUP) && pathname.startsWith('/upload-data')) return true;
    return false;
}

/**
 * Home-page tiles. Visibility must match route access: limited Manning/Upload
 * users only see their own modules; other tiles require the matching group.
 */
export const HOME_TILES = Object.freeze([
    {
        id: 'ai',
        path: '/ai',
        icon: '🤖',
        title: 'T-6 A/B AI Assistant',
        description: "Chat with the Amentum's AI Assistant",
        allowedGroups: ['AI'],
        allowStaff: true,
    },
    {
        id: 'legion',
        path: '/legion',
        icon: '💬',
        title: 'Legion',
        description: 'Company network assistant',
        allowedGroups: ['AI'],
        allowStaff: true,
    },
    {
        id: 'status-board',
        path: '/status-board',
        icon: '✈️',
        title: 'Aircraft Status Board',
        description: 'Aircraft Status',
    },
    {
        id: 'aircraft-summary',
        path: '/aircraft-summary',
        icon: '📊',
        title: 'Aircraft Summary',
        description: 'Aircraft Summary Report',
    },
    {
        id: 'inspections',
        path: '/inspections',
        icon: '📋',
        title: 'Inspections',
        description: 'View and manage all inspections',
    },
    {
        id: 'reports',
        path: '/reports',
        icon: '📑',
        title: 'Reports',
        description: 'High time component, phase, and strut waterfalls',
        allowedGroups: ['Supervisor'],
        allowStaff: true,
    },
    {
        id: 'manning',
        path: '/manning',
        icon: '👥',
        title: 'Manning',
        description: 'Employees, work centers, and assignments',
        allowedGroups: ['Supervisor', MANNING_GROUP],
        allowStaff: true,
    },
    {
        id: 'upload-data',
        path: '/upload-data',
        icon: '📤',
        title: 'Upload Data',
        description: 'Upload CRD, daily AC time, and other reports',
        allowedGroups: ['Supervisor', UPLOAD_GROUP],
        allowStaff: true,
    },
    {
        id: 'flight-planning',
        path: '/flight-planning',
        icon: '📅',
        title: 'Flight Planning',
        description: 'Schedule, tail helper, conflicts, utilization',
        allowedGroups: ['Supervisor', 'AMPT', 'Scheduler', 'Flight Planning'],
        allowStaff: true,
    },
    {
        id: 'executive-dashboard',
        path: '/executive-dashboard',
        icon: '📊📈',
        title: 'Executive Dashboard',
        description: 'KPIs and Analytics',
        allowedGroups: ['Supervisor'],
        allowStaff: true,
    },
    {
        id: 'slmm',
        path: '/slmm',
        icon: '📋',
        title: 'SLMM',
        description: 'Service Life Maintenance Management',
        allowedGroups: ['Supervisor'],
        allowStaff: true,
    },
    {
        id: 'control-tower',
        path: '/control-tower',
        icon: '📡🗺️',
        title: 'Control Tower',
        description: 'Live ADS-B aircraft tracking',
        allowedGroups: ['AMSS'],
        allowStaff: true,
    },
    {
        id: 'ampt',
        path: '/maintenance-priority',
        icon: '📈',
        title: 'A.M.P.T',
        description: 'Calculate maintenance priorities',
        allowedGroups: ['AMPT'],
        allowStaff: true,
    },
    {
        id: 'parts',
        path: '/parts',
        icon: '🛠️🔩',
        title: 'Parts',
        description: 'Manage inventory, tracked parts, and ordering',
        allowStaff: true,
    },
    {
        id: 'admin',
        path: '/admin-dashboard',
        icon: '⚙️',
        title: 'Admin',
        description: 'Users, aircraft, and system configuration',
        staffOnly: true,
    },
]);

export function canSeeHomeTile(tile, { isStaff, groups = [] } = {}) {
    const auth = { isStaff, groups: Array.isArray(groups) ? groups : [] };
    if (tile.staffOnly) return Boolean(auth.isStaff);
    if (!isPathAllowedForUser(tile.path, auth)) return false;
    const allowedGroups = tile.allowedGroups || [];
    if (!allowedGroups.length) return true;
    if (tile.allowStaff && auth.isStaff) return true;
    return allowedGroups.some((name) => auth.groups.includes(name));
}

export function visibleHomeTiles(auth) {
    return HOME_TILES.filter((tile) => canSeeHomeTile(tile, auth));
}
